/**
 * #58 requirement 6 (USER ORDER 2026-08-06): strip "=== Section N: Title ==="
 * scaffolding at render time, leaving the title rendered as a header.
 *
 * WHY A RENDER-TIME GUARD AND NOT JUST A PROMPT FIX
 * The generator prompt is being corrected so new summaries stop emitting this
 * wrapper. That alone would not help a single existing patient: every summary
 * already written sits in Postgres with the old text, and would keep showing
 * raw "===" on screen until it was regenerated. Fixing the prompt makes new
 * output correct at the source; this makes ALL output correct on screen,
 * immediately, and keeps it correct if the model ever drifts back. Deliberately
 * belt and braces.
 *
 * RULE 12 CLEARANCE - read before changing this
 * The match is on the DELIMITER SHAPE ("===" wrapping a line) and an optional
 * "Section N:" numbering prefix. That is a structural/typographic signal, which
 * rule 12 explicitly permits. It must NEVER key off the section titles
 * themselves -- no list of "Medical History", "SDOH" and friends. Such a list
 * would be exactly the hardcoded keyword-bound logic the no-keyword-bound guard
 * suite exists to prevent. If you find yourself adding known headings here,
 * stop: the design is wrong, not the list.
 *
 * WHAT IT MUST NOT DO
 * Only a line whose ENTIRE trimmed content is the delimited form is rewritten.
 * A sentence that merely contains "===" somewhere in the middle is ordinary
 * clinical prose and is left exactly as it is -- silently eating real content
 * to tidy up formatting would be far worse than showing a stray delimiter.
 */

const DELIMITER = '===';

/**
 * The marker that replaced the "=== ... ===" dividers inside the generator
 * spec (#58 req 5). The spec uses these to label its own instruction blocks;
 * the model is told never to reproduce them.
 *
 * Stripped here as well, for one specific reason. Swapping the divider style
 * fixes the leak at the source ONLY if the model obeys, and that could not be
 * verified before shipping: the self-hosted vLLM is firewall-closed to this
 * machine, and the dev container still runs the previous image, so a
 * pre-deploy live probe was not possible (recorded as a residual rather than
 * quietly skipped). If the model does echo the new marker, the clinician would
 * see raw instruction scaffolding in a clinical summary -- a worse failure
 * than the "===" this ticket set out to remove. Covering it costs one extra
 * prefix check.
 *
 * Safe to match: a line whose ENTIRE content is this bracketed marker is never
 * clinical prose. As with the delimiter, a line that merely mentions it stays
 * untouched, and no section TITLE is ever matched (rule 12).
 */
const INSTRUCTION_MARKER_PREFIX = '[INSTRUCTIONS';

/**
 * The title inside a delimited header line, or null when the line is not one.
 *
 * Exported for direct testing: the decision of "is this a header?" is the part
 * that can go wrong, so it is worth asserting on its own rather than only
 * through the rewritten text.
 */
export function sectionHeaderTitle(line: string): string | null {
  const trimmed = line.trim();
  if (trimmed.length <= DELIMITER.length * 2) return null;
  if (!trimmed.startsWith(DELIMITER) || !trimmed.endsWith(DELIMITER)) return null;

  const inner = trimmed.slice(DELIMITER.length, -DELIMITER.length).trim();
  if (!inner) return null;

  // Drop a leading "Section 1:" / "Section 12:" if present. Only when the part
  // before the colon is exactly one word followed by a number, so a genuine
  // title that happens to contain a colon survives intact.
  const colon = inner.indexOf(':');
  if (colon > 0) {
    const head = inner.slice(0, colon).trim().split(/\s+/);
    const tail = inner.slice(colon + 1).trim();
    if (tail && head.length === 2 && /^\d+$/.test(head[1])) {
      return tail;
    }
  }
  return inner;
}

/**
 * Rewrite any delimited header lines in `text` as bold markdown headers.
 *
 * A no-op on text containing no delimiters at all, so it is safe to run over
 * every paragraph the renderer touches rather than guessing which ones came
 * from the summary generator.
 */
export function stripSectionScaffolding(text: string | null | undefined): string {
  const value = String(text ?? '');
  if (!value.includes(DELIMITER) && !value.includes(INSTRUCTION_MARKER_PREFIX)) return value;

  return value
    .split('\n')
    .filter((line) => !isLeakedInstructionMarker(line))
    .map((line) => {
      const title = sectionHeaderTitle(line);
      return title === null ? line : `**${title}**`;
    })
    .join('\n');
}

/**
 * A whole line that is nothing but a leaked "[INSTRUCTIONS -- ...]" marker.
 *
 * Dropped entirely rather than turned into a header: unlike a section
 * delimiter, this carries no title a clinician should see -- it is a fragment
 * of the instructions, not a heading for the content beneath it.
 */
function isLeakedInstructionMarker(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith(INSTRUCTION_MARKER_PREFIX) && trimmed.endsWith(']');
}
